"use client";

import { useState } from "react";
import { cn } from "../lib/utils";

interface OptionType {
  label: string;
  value: any;
}

interface ButtonGroupSelectProps {
  label: string;
  options: OptionType[];
  onChange: (selected: any) => void;
  initialSelectedValue?: any;
  /**
   * Makes the group controlled: the selection follows this instead of its own
   * state (e.g. when the value is restored from the URL after mount).
   */
  value?: any;
  className?: string;
  disabled?: boolean;
  /**
   * For filter bars: buttons size to their labels instead of stretching, never
   * wrap, and the group matches the 44px height and label spacing of
   * TextInput / SelectV2 so it lines up beside them.
   */
  inline?: boolean;
}

export function ButtonGroupSelect({
  label,
  options,
  onChange,
  initialSelectedValue,
  value,
  className = "",
  disabled = false,
  inline = false,
}: ButtonGroupSelectProps) {
  const [selected, setSelected] = useState<any>(
    initialSelectedValue !== undefined
      ? initialSelectedValue
      : options[0]?.value,
  );

  const current = value !== undefined ? value : selected;

  const handleSelect = (option: any) => {
    if (disabled) return;
    setSelected(option);
    onChange(option);
  };

  return (
    <div
      className={cn(
        inline ? "flex flex-col" : "flex flex-col gap-2",
        disabled ? "opacity-50 pointer-events-none" : "",
        className,
      )}
    >
      <label className="text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="inline-flex rounded-md shadow-xs">
        {options.map((option, index) => (
          <button
            key={index}
            type="button"
            onClick={() => handleSelect(option.value)}
            className={`
              relative ${inline ? "min-h-[44px] whitespace-nowrap" : "w-full"} ${index > 0 ? "-ml-px" : ""} 
              inline-flex items-center 
              ${index === 0 ? "rounded-l-md" : ""} 
              ${index === options.length - 1 ? "rounded-r-md" : ""}
              px-3 py-2 text-sm font-semibold
              ${
                current === option.value
                  ? "bg-blue-500 text-white"
                  : "bg-white text-gray-900 hover:bg-gray-50"
              }
              ring-1 ring-inset ring-gray-300
              focus:z-10
            `}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}
